import { QdsButtonBase, type QdsButtonVariant } from '../shared/button-base.js';
import { isAttrTruthy, reflectBoolean, upgradeProperty } from '../shared/form-control.js';

/**
 * A stable-label, two-state action button with native form participation.
 * @element qds-toggle-button
 * @attr {string} label - Visible fallback label; keep the label stable when toggled.
 * @attr {string} aria-label - Explicit accessible name.
 * @attr {boolean} checked - Default/reset state; the checked property is current state.
 * @attr {string} value - Submission value when checked; default on.
 * @attr {string} name - Submitted field name.
 * @attr {string} form - Associated form ID.
 * @attr {boolean} disabled - Blocks activation and form submission.
 * @attr {boolean} loading - Busy indicator; blocks repeated activation.
 * @attr {boolean} readonly - Keeps the state and remains keyboard focusable.
 * @attr {'primary'|'secondary'|'quiet'|'destructive'} variant - Visual treatment; default secondary.
 * @attr {'sm'|'md'|'lg'} size - Visual size; target remains at least 44px.
 * @property {boolean} checked - Current boolean; programmatic changes are silent.
 * @property {boolean} defaultChecked - Attribute-backed reset state.
 * @property {string} value - Native submission value, separate from boolean state.
 * @property {boolean} readonly - Prevent user changes while retaining focus and form value.
 * @slot - Stable action label.
 * @slot icon - Decorative leading icon.
 * @slot suffix - Decorative trailing content.
 * @csspart button - Native button with aria-pressed.
 * @csspart label - Label slot.
 * @csspart icon - Leading icon slot.
 * @csspart suffix - Trailing slot.
 * @csspart spinner - Busy indicator.
 * @fires click - Cancelable activation; preventDefault cancels the toggle.
 * @fires input - User toggle; detail { component, checked, value }.
 * @fires change - User toggle; detail { component, checked, value }.
 */
export class QdsToggleButton extends QdsButtonBase {
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'checked', 'readonly']; }
  private _checked = false;
  private _dirty = false;
  protected override get _defaultVariant(): QdsButtonVariant { return 'secondary'; }
  get checked(): boolean { return this._checked; }
  set checked(value: boolean) { this._dirty = true; this._checked = Boolean(value); this._updateIcon(); }
  get defaultChecked(): boolean { return isAttrTruthy(this.getAttribute('checked')); }
  set defaultChecked(value: boolean) { reflectBoolean(this, 'checked', value); }
  override get value(): string { return this.getAttribute('value') ?? 'on'; }
  override set value(value: string) { this.setAttribute('value', String(value ?? '')); }
  get readonly(): boolean { return isAttrTruthy(this.getAttribute('readonly')); }
  set readonly(value: boolean) { reflectBoolean(this, 'readonly', value); }
  override connectedCallback(): void {
    for (const name of ['checked', 'defaultChecked', 'readonly']) upgradeProperty(this, name);
    if (!this._dirty) this._checked = this.defaultChecked;
    super.connectedCallback();
    if (!this.shadowRoot!.querySelector('[data-toggle-styles]')) {
      const style = document.createElement('style'); style.dataset.toggleStyles = '';
      style.textContent = `button[aria-pressed=true] { background: var(--theme-accent-primary, #445e89); color: var(--theme-text-on-accent-primary, #fff); border-color: var(--theme-accent-primary, #445e89); }
        @media (forced-colors: active) { button[aria-pressed=true] { background: Highlight; color: HighlightText; border-color: Highlight; } }`;
      this.shadowRoot!.append(style);
    }
  }
  override attributeChangedCallback(): void {
    if (!this._dirty) this._checked = this.defaultChecked;
    super.attributeChangedCallback();
  }
  protected override _updateIcon(): void {
    this._button?.setAttribute('aria-pressed', String(this.checked));
    // Readonly is exposed on the host API; button roles do not support aria-readonly.
    this._internals?.setFormValue(this.checked && !this.disabled ? this.value : null, this.checked ? 'checked' : 'unchecked');
  }
  protected override _activate(): boolean {
    if (this.readonly) return false;
    this.checked = !this.checked;
    for (const name of ['input', 'change']) this.dispatchEvent(new CustomEvent(name, {
      bubbles: true, composed: true, detail: { component: this, checked: this.checked, value: this.value },
    }));
    return false;
  }
  formResetCallback(): void { this._dirty = false; this._checked = this.defaultChecked; this._updateIcon(); }
  formStateRestoreCallback(state: string): void { this.checked = state === 'checked'; }
}
if (!customElements.get('qds-toggle-button')) customElements.define('qds-toggle-button', QdsToggleButton);
